import * as yup from 'yup';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-form-state-manager';
import { useTranslation } from 'react-i18next';

import { Event } from './helpers';
import { ImageFile, ImagesInput } from '../../../../common/Common/UploadButton';
import { TranslatableEditor, TranslatableInput } from '../../../Translations/Inputs';
import { emailRegex, getServerErrors, validateUrl } from '../../../../common/helpers';
import { translationsForAttribute } from '../../../Translations/helpers';
import DateRangeForm, { dateRangeObject } from '../../../Common/DateRangeForm';
import EventPicker from '../../EventPicker';
import GetGeneratedEventDescriptionQuery from './GetGeneratedEventDescriptionQuery';
import UI from '../../../../common/UI';
import WebsiteInput from '../../../Common/WebsiteInput';
import useEditEventMutation from './useEditEventMutation';
import useLazyQuery from '../../../../api/useLazyQuery';
import useNavigationPrompt from '../../../../common/useNavigationPrompt';
import useProject from '../../../useProject';
import useValidation from '../../../../common/useValidation';

const editEventInfoSchema = yup.object().shape({
  title: yup.string().required(),
  usp_1: yup.string().nullable().when(['usp_2'], {
    is: (value: string) => value,
    then: yup.string().required(),
  }).when(['usp_3'], {
    is: (value: string) => value,
    then: yup.string().required(),
  }),
  usp_2: yup.string().nullable().when(['usp_3'], {
    is: (value: string) => value,
    then: yup.string().required(),
  }),
  usp_3: yup.string().nullable(),
  ...dateRangeObject(true, false),
  location: yup.object().required().shape({
    id: yup.string().required(),
  }),
  website_url: yup.string().nullable().test((value) => !value || validateUrl(value)),
  email: yup.string().nullable().matches(emailRegex, { excludeEmptyString: true, name: 'email' }),
});

export interface EditEventInfoFormProps {
  event: Event;
}

const EditEventInfoForm = ({ event }: EditEventInfoFormProps) => {
  const { t } = useTranslation();
  const project = useProject();

  const getValues = (event: Event) => ({
    title: event.title,
    title_translations: translationsForAttribute(event.translations, 'title'),
    photos: event.photos.map((photo) => ({ id: photo.file.id, url: photo.file.url })),
    description: event.description,
    description_translations: translationsForAttribute(event.translations, 'description'),
    usp_1: event.usp_1 || '',
    usp_1_translations: translationsForAttribute(event.translations, 'usp_1'),
    usp_2: event.usp_2 || '',
    usp_2_translations: translationsForAttribute(event.translations, 'usp_2'),
    usp_3: event.usp_3 || '',
    usp_3_translations: translationsForAttribute(event.translations, 'usp_3'),
    email: event.email,
    website_url: event.website_url,
    previous_edition_id: event.previous_edition?.id,
    location: {
      id: event.location.id,
      title: event.location.title,
      description: event.location.description,
      address: event.location.address,
      country: event.location.country,
      coordinates: event.location.coordinates,
    },
    start_date: event.start_date,
    end_date: event.start_date === event.end_date ? null : event.end_date,
    private: event.private,
  });

  const form = useForm({
    values: getValues(event),
  });

  useNavigationPrompt(form.changed());

  // Re-initialize Tiptap editors after saving.
  const [formKey, setFormKey] = useState(0);

  const [editEvent, { error, loading }] = useEditEventMutation({
    input: {
      id: event.id,
      info: {
        ...form.values,
        photos: form.values.photos.map(({ id }) => id),
        end_date: form.values.end_date || form.values.start_date,
      },
    },
    onSuccess: (event) => {
      form.reset(getValues(event));
      setFormKey((key) => key + 1);
    },
  });

  const locationBlurTimerRef = useRef<number>();

  // Clear timer on unmount.
  useEffect(() => () => window.clearTimeout(locationBlurTimerRef.current), []);

  const handleLocationBlur = () => {
    window.clearTimeout(locationBlurTimerRef.current);

    // Save the touched event after a small delay, to prevent flashing of validation errors.
    locationBlurTimerRef.current = window.setTimeout(() => {
      form.setTouched('location', true);
    }, 300);
  };

  const { valid, errors } = useValidation({
    schema: editEventInfoSchema,
    values: form.values,
    externalErrors: getServerErrors(error, 'info'),
  });

  const handleUpload = (file: ImageFile) => {
    form.set((values) => ({ ...values, photos: [...values.photos, file] }));
  };

  const handlePhotosChange = (files: ImageFile[]) => {
    form.set((values) => ({ ...values, photos: files }));
  };

  const [
    generateEventDescription,
  ] = useLazyQuery(GetGeneratedEventDescriptionQuery);

  const [generatingEventDescription, setGeneratingEventDescription] = useState(false);
  const eventDescriptionValueRef = useRef(form.values.description);
  const eventDescriptionInputKeyRef = useRef(0);

  const handleGenerateDescription = useCallback(() => {
    eventDescriptionValueRef.current = form.values.description;

    setGeneratingEventDescription(true);

    generateEventDescription({
      variables: {
        project_id: project.id,
        input: {
          event_id: event.id,
          title: form.values.title,
          start_date: form.values.start_date,
          end_date: form.values.end_date,
          location: form.values.location,
          website_url: form.values.website_url,
          locale: event.locale,
          generate_description: true,
          generate_usps: false,
        },
      },
    }).then((result) => {
      setGeneratingEventDescription(false);

      if (!result.data) {
        return;
      }

      const newDescription = result.data.project.generated_event_description;

      eventDescriptionInputKeyRef.current++;
      eventDescriptionValueRef.current = newDescription.description;

      form.set((values) => ({
        ...values,
        description: newDescription.description,
      }));
    });
  }, [
    project.id, event.id, event.locale, form.values.title, form.values.start_date, form.values.end_date,
    form.values.location, form.values.website_url, form.values.description,
    generateEventDescription,
  ]);

  const [generatingEventUsps, setGeneratingEventUsps] = useState(false);
  const currentUspsValue = [form.values.usp_1, form.values.usp_2, form.values.usp_3].join('|');
  const eventUspsValueRef = useRef(currentUspsValue);
  const eventUspsInputKeyRef = useRef(0);

  const handleGenerateUsps = useCallback(() => {
    eventUspsValueRef.current = currentUspsValue;

    setGeneratingEventUsps(true);

    generateEventDescription({
      variables: {
        project_id: project.id,
        input: {
          event_id: event.id,
          title: form.values.title,
          start_date: form.values.start_date,
          end_date: form.values.end_date,
          location: form.values.location,
          website_url: form.values.website_url,
          locale: event.locale,
          generate_description: false,
          generate_usps: true,
        },
      },
    }).then((result) => {
      setGeneratingEventUsps(false);

      if (!result.data) {
        return;
      }

      const newDescription = result.data.project.generated_event_description;

      eventUspsInputKeyRef.current++;
      eventUspsValueRef.current = [newDescription.usp_1, newDescription.usp_2, newDescription.usp_3].join('|');

      form.set((values) => ({
        ...values,
        usp_1: newDescription.usp_1,
        usp_2: newDescription.usp_2,
        usp_3: newDescription.usp_3,
      }));
    });
  }, [
    project.id, event.id, event.locale, form.values.title, form.values.start_date, form.values.end_date,
    form.values.location, form.values.website_url, currentUspsValue,
    generateEventDescription,
  ]);

  const hasValidWebsiteUrl = form.values.website_url && !errors.website_url;

  const maxPhotos = 10;

  return (
    <UI.Form onSubmit={() => editEvent()} key={formKey}>
      <UI.FormGrid>
        <UI.ServerErrors error={error} />

        <UI.Div>
          <UI.Legend>
            {t('basic_info')}
          </UI.Legend>
        </UI.Div>

        <UI.FormGrid>
          <UI.InputGroup sc={{ valid: !errors.title, touched: form.touched.title }}>
            <UI.InputLabel htmlFor="event_title">
              {t('event_title')}
              {' '}
              <UI.RequiredMark />
            </UI.InputLabel>
            <TranslatableInput
              form={form}
              errors={errors}
              name="title"
              id="event_title"
              sc={{ size: 'lg' }}
              defaultLocale={event.locale}
              placeholder={t('event_title_placeholder')}
            />
            <UI.ErrorMessages attribute={t('event_title')} errors={errors.title} />
          </UI.InputGroup>

          <DateRangeForm form={form} errors={errors} requireStart allowFullDay />

          <UI.InputGroup sc={{ valid: !errors['location.id'], touched: form.touched.location }}>
            <UI.InputLabel htmlFor="location">
              {t('location')}
              {' '}
              <UI.RequiredMark />
            </UI.InputLabel>
            <UI.GoogleLocationInput
              id="location"
              value={form.values.location}
              onChange={(location) => form.set('location', location)}
              onBlur={() => handleLocationBlur()}
              placeholder={t('location_placeholder')}
            />
            <UI.ErrorMessages attribute={t('location')} errors={errors['location.id']} />
          </UI.InputGroup>

          <UI.InputGroup sc={{ valid: !errors.website_url, touched: form.touched.website_url }}>
            <UI.InputLabel htmlFor="website">
              {t('website')}
            </UI.InputLabel>
            <WebsiteInput form={form} formKey="website_url" id="website" />
            <UI.ErrorMessages attribute={t('website')} errors={errors.website_url} />
          </UI.InputGroup>

          <UI.InputGroup style={{ zIndex: 20 }}>
            <UI.InputLabel>
              {t('previous_edition')}
            </UI.InputLabel>
            <EventPicker
              eventId={form.values.previous_edition_id}
              onChange={(event) => form.set('previous_edition_id', event?.id || null)}
              nullableLabel={t('none')}
              startDateBefore={event.start_date}
            />
          </UI.InputGroup>

          <UI.HR />

          <UI.Div>
            <UI.Legend sc={{ mb: 1 }}>
              {t('about_the_event')}
            </UI.Legend>
            <UI.InputDescription>
              {t('about_the_event_description')}
            </UI.InputDescription>
          </UI.Div>

          <UI.InputGroup sc={{ valid: !errors.photos, touched: form.touched.photos }}>
            <UI.InputLabel>
              {t('event_photos')}
            </UI.InputLabel>
            <UI.InputDescription>
              {t('event_photos_description', { max: maxPhotos })}
              {' '}
              {t('image_recommendations_description')}
            </UI.InputDescription>
            <ImagesInput
              value={form.values.photos}
              onUpload={handleUpload}
              onChange={handlePhotosChange}
              max={maxPhotos}
            />
            <UI.ErrorMessages attribute={t('event_photos')} errors={errors.photos} />
          </UI.InputGroup>

          <UI.InputGroup
            sc={{
              valid: !errors.usp_1 && !errors.usp_2 && !errors.usp_3,
              touched: form.touched.usp_1 || form.touched.usp_2 || form.touched.usp_3,
            }}
            key={`usps_${eventUspsInputKeyRef.current}`}
          >
            <UI.FlexContainer sc={{ alignItems: 'center' }}>
              <UI.InputLabel htmlFor="event_usp_1">
                {t('event_highlights')}
              </UI.InputLabel>
              <UI.Button
                sc={{ blank: true, size: 'xs', ml: 1, my: -0.5 }}
                disabled={generatingEventUsps || !hasValidWebsiteUrl}
                onClick={handleGenerateUsps}
              >
                <UI.Icon>
                  {generatingEventUsps ? <UI.Loader sc={{ brand: 'gray.800', size: 12 }} /> : <UI.Icons.Ai />}
                </UI.Icon>
                {' '}
                {t('generate')}
              </UI.Button>
            </UI.FlexContainer>
            <UI.InputDescription>
              {t('event_highlights_description')}
            </UI.InputDescription>
            <UI.FormGrid sc={{ gutter: 0.25 }}>
              <TranslatableInput
                form={form}
                errors={errors}
                name="usp_1"
                id="event_usp_1"
                title={t('event_usp_1')}
                defaultLocale={event.locale}
                maxLength={50}
                sc={{ pl: 6 }}
                disabled={generatingEventUsps}
                inputPrefix={(
                  <UI.Badge sc={{ outline: true }}>
                    1
                  </UI.Badge>
                )}
              />
              <TranslatableInput
                form={form}
                errors={errors}
                name="usp_2"
                id="event_usp_2"
                title={t('event_usp_2')}
                defaultLocale={event.locale}
                maxLength={50}
                sc={{ pl: 6 }}
                disabled={generatingEventUsps}
                inputPrefix={(
                  <UI.Badge sc={{ outline: true }}>
                    2
                  </UI.Badge>
                )}
              />
              <TranslatableInput
                form={form}
                errors={errors}
                name="usp_3"
                id="event_usp_3"
                title={t('event_usp_3')}
                defaultLocale={event.locale}
                maxLength={50}
                sc={{ pl: 6 }}
                disabled={generatingEventUsps}
                inputPrefix={(
                  <UI.Badge sc={{ outline: true }}>
                    3
                  </UI.Badge>
                )}
              />
            </UI.FormGrid>
            <UI.ErrorMessages attribute={t('event_usp_1')} errors={errors.usp_1} />
            <UI.ErrorMessages attribute={t('event_usp_2')} errors={errors.usp_2} />
            <UI.ErrorMessages attribute={t('event_usp_3')} errors={errors.usp_3} />
          </UI.InputGroup>

          <UI.InputGroup
            sc={{ valid: !errors.description, touched: form.touched.description }}
            key={eventDescriptionInputKeyRef.current}
          >
            <UI.FlexContainer sc={{ alignItems: 'center' }}>
              <UI.InputLabel id="EventDescriptionLabel">
                {t('event_description')}
              </UI.InputLabel>
              <UI.Button
                sc={{ blank: true, size: 'xs', ml: 1, my: -0.5 }}
                disabled={generatingEventDescription || !hasValidWebsiteUrl}
                onClick={handleGenerateDescription}
              >
                <UI.Icon>
                  {generatingEventDescription ? <UI.Loader sc={{ brand: 'gray.800', size: 12 }} /> : <UI.Icons.Ai />}
                </UI.Icon>
                {' '}
                {t('generate')}
              </UI.Button>
            </UI.FlexContainer>
            <UI.InputDescription>
              {t('event_description_description')}
            </UI.InputDescription>
            <TranslatableEditor
              form={form}
              errors={errors}
              name="description"
              defaultLocale={event.locale}
              title={t('event_description')}
              options={{
                headings: [3],
              }}
              attributes={{
                'aria-labelledby': 'EventDescriptionLabel',
              }}
            />
            <UI.ErrorMessages attribute={t('description')} errors={errors.description} />
          </UI.InputGroup>

          <UI.HR />

          <UI.Div>
            <UI.Legend>
              {t('email')}
            </UI.Legend>
          </UI.Div>

          <UI.InputGroup sc={{ valid: !errors.email, touched: form.touched.email }}>
            <UI.InputLabel htmlFor="email_address">
              {t('email_address')}
            </UI.InputLabel>
            <UI.InputDescription>
              {t('event_email_description')}
            </UI.InputDescription>
            <UI.DebouncedInput
              {...form.text('email')}
              placeholder={project.organisation_email}
              id="email_address"
            />
            <UI.ErrorMessages attribute={t('email_address')} errors={errors.email} />
          </UI.InputGroup>

          <UI.HR />

          <UI.Div>
            <UI.Legend>
              {t('visibility')}
            </UI.Legend>
          </UI.Div>

          <UI.InputGroup sc={{ valid: !errors.private, touched: form.touched.private }}>
            <UI.InputLabel htmlFor="private">
              {t('event_visibility')}
            </UI.InputLabel>
            <UI.InputDescription>
              {t('private_event_description')}
            </UI.InputDescription>
            <UI.Checkbox {...form.checkbox('private')} id="private">
              {t('private_event')}
            </UI.Checkbox>
          </UI.InputGroup>
        </UI.FormGrid>

        <UI.HR />

        <UI.FormButtons
          form={form}
          submitLabel={t('save_event')}
          loading={loading}
          valid={valid}
        />
      </UI.FormGrid>
    </UI.Form>
  );
};

export default EditEventInfoForm;
